import {type ReactNode, useEffect, useLayoutEffect, useRef} from 'react';
import {createAnimatable, type Animatable} from './animatable';
import {useDockConfig} from './DockContext';
import type {DockDirection, DockItemHandle, DockPosition} from './types';

/** Pointer-driven scale is deliberately snappy; base resize is smoother. */
const SCALE_DURATION = 100;
const RESIZE_DURATION = 200;
const RESIZE_EXIT_DURATION = 160;
const RESIZE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export interface DockItemProps {
  id: string;
  /** Tooltip text. Also used as the accessible name. */
  label?: string;
  /** Override the shared size on the horizontal axis (widgets, thumbnails). */
  width?: number;
  /** Override the shared size on the vertical axis. */
  height?: number;
  /** Rendered in a layer that follows the footprint but escapes the scale transform. */
  indicator?: ReactNode;
  /** Suppress the tooltip (used by minimized-window thumbnails). */
  hideTooltip?: boolean;
  /** Flip to `true` to play the launch bounce once. */
  bouncing?: boolean;
  className?: string;
  children: ReactNode;
  onActivate?: (id: string) => void;
}

interface Geometry extends Record<string, number> {
  width: number;
  height: number;
  scale: number;
}

/**
 * The animated cell.
 *
 * Two-layer model:
 *   - the outer `<li>` owns the LAYOUT FOOTPRINT (base * scale) so neighbours
 *     get pushed aside as an item inflates;
 *   - the inner `.dock-item__scale` owns the VISUAL, transformed with
 *     `scale()` so the artwork stays crisp and composited.
 *
 * The indicator and tooltip live in a third layer that tracks the footprint but
 * sits outside the scale transform — that is why a running dot stays 6px tall
 * while its icon doubles in size.
 */
export function DockItem({
  id,
  label,
  width,
  height,
  indicator,
  hideTooltip,
  bouncing,
  className,
  children,
  onActivate,
}: DockItemProps) {
  const config = useDockConfig();
  const liRef = useRef<HTMLLIElement>(null);
  const posRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const animatableRef = useRef<Animatable<Geometry> | null>(null);
  const bounceRef = useRef<Animation | null>(null);
  const handleRef = useRef<DockItemHandle | null>(null);

  const widthValue = Number.isFinite(width) && (width as number) > 0 ? (width as number) : config.size;
  const heightValue =
    Number.isFinite(height) && (height as number) > 0 ? (height as number) : config.size;

  // Keep the latest base sizes readable from the imperative handle without
  // re-registering it on every prop change.
  const baseRef = useRef({width: widthValue, height: heightValue});
  baseRef.current = {width: widthValue, height: heightValue};

  // The wrapper compares against the *requested* scale, not the interpolated
  // one — otherwise every frame looks like a change and retargets the tween.
  const targetScaleRef = useRef(1);
  const easingRef = useRef(config.easing);
  easingRef.current = config.easing;

  useLayoutEffect(() => {
    const li = liRef.current;
    const scaleEl = scaleRef.current;
    const posEl = posRef.current;
    if (!li || !scaleEl || !posEl) return;

    const animatable = createAnimatable<Geometry>(
      {width: baseRef.current.width, height: baseRef.current.height, scale: 1},
      {
        onRender(state) {
          // Single write pass per frame: footprint first, then the visual.
          li.style.width = `${state.width * state.scale}px`;
          li.style.height = `${state.height * state.scale}px`;
          scaleEl.style.width = `${state.width}px`;
          scaleEl.style.height = `${state.height}px`;
          scaleEl.style.transform = `scale(${state.scale})`;
        },
      },
    );
    animatableRef.current = animatable;

    // Paint the initial geometry synchronously so there is no first-frame flash.
    animatable.snap('width', baseRef.current.width);
    animatable.snap('height', baseRef.current.height);
    animatable.snap('scale', 1);

    const handle: DockItemHandle = {
      el: li,
      baseSize: (direction: DockDirection) =>
        direction === 'horizontal' ? baseRef.current.width : baseRef.current.height,
      scale: () => targetScaleRef.current,
      setScale: (next: number) => {
        targetScaleRef.current = next;
        animatable.set('scale', next, SCALE_DURATION, easingRef.current);
      },
      bounce: (direction: DockDirection, position: DockPosition) => {
        bounceRef.current?.cancel();
        const distance = Math.round(baseRef.current.height * 0.55);
        const axis = direction === 'horizontal' ? 'translateY' : 'translateX';
        const sign = position === 'bottom' || position === 'right' ? -1 : 1;
        const up = `${axis}(${sign * distance}px)`;
        const half = `${axis}(${sign * Math.round(distance * 0.35)}px)`;
        bounceRef.current = posEl.animate(
          [
            {transform: 'translate(-50%, -50%)'},
            {transform: `translate(-50%, -50%) ${up}`, offset: 0.35},
            {transform: 'translate(-50%, -50%)', offset: 0.62},
            {transform: `translate(-50%, -50%) ${half}`, offset: 0.82},
            {transform: 'translate(-50%, -50%)'},
          ],
          {duration: 900, easing: 'cubic-bezier(0.28, 0.84, 0.42, 1)'},
        );
      },
    };

    handleRef.current = handle;
    const unregister = config.register(id, handle);
    return () => {
      unregister();
      handleRef.current = null;
      animatable.dispose();
      animatableRef.current = null;
    };
    // `config.register` is stable; easing is read fresh inside setScale via closure
    // over `config`, so we intentionally re-register when the dock identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, config.register]);

  // Base-size changes ride the SAME channel as magnification, so an item that is
  // currently magnified resizes without snapping back to scale 1 first.
  useEffect(() => {
    const animatable = animatableRef.current;
    if (!animatable) return;
    const growing =
      widthValue > animatable.state.width || heightValue > animatable.state.height;
    const duration = growing ? RESIZE_DURATION : RESIZE_EXIT_DURATION;
    animatable.set('width', widthValue, duration, RESIZE_EASE);
    animatable.set('height', heightValue, duration, RESIZE_EASE);
  }, [widthValue, heightValue]);

  useEffect(() => {
    if (!bouncing) return;
    handleRef.current?.bounce(config.direction, config.position);
  }, [bouncing, config.direction, config.position]);

  useLayoutEffect(() => {
    liRef.current?.style.setProperty('--gap', `${config.gap}px`);
  }, [config.gap]);

  useLayoutEffect(() => {
    liRef.current?.style.setProperty(
      '--dock-item-will-change',
      config.willChange ? 'width, height' : 'auto',
    );
  }, [config.willChange]);

  return (
    <li
      ref={liRef}
      data-dock-item=""
      data-dock-id={id}
      className={`dock-item dock-item--${config.direction} ${className ?? ''}`}
    >
      <div ref={posRef} className="dock-item__pos">
        <div
          ref={scaleRef}
          className="dock-item__scale"
          role={onActivate ? 'button' : undefined}
          tabIndex={onActivate ? 0 : undefined}
          aria-label={label}
          title={undefined}
          onClick={onActivate ? () => onActivate(id) : undefined}
          onKeyDown={
            onActivate
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onActivate(id);
                  }
                }
              : undefined
          }
        >
          {children}
        </div>
      </div>

      <div className="dock-item__indicator" aria-hidden="true">
        {indicator}
      </div>

      {label && !hideTooltip ? (
        <div className={`dock-tooltip dock-tooltip--${config.position}`} aria-hidden="true">
          <span>{label}</span>
        </div>
      ) : null}
    </li>
  );
}
